import type { Maybe } from '@adornis/base/utilTypes.js';

/**
 * Caches the result of a async operation that is related to some key. This can be used to fetch foreign
 * data in a export field and don't re-fetch it in another fields that needs that data.
 * @param fetchFn The function to fetch something by a key
 * @returns Maybe cached value if the fetch function returned something truthy
 */
export function newOperationCache<T>(fetchFn: (id: string) => Promise<Maybe<T>>) {
  const cache: Record<string, Maybe<T>> = {};

  const get = async (id: string): Promise<Maybe<T>> => {
    if (id === '') {
      return null;
    }

    if (cache[id]) {
      return cache[id];
    }

    const elem = await fetchFn(id);
    cache[id] = elem;
    return elem;
  };

  const getStored = (id?: Maybe<string>): Maybe<T> => {
    if (!id) {
      return null;
    }
    return cache[id] || null;
  };

  const toString = async (id: string, fmt: (elem: T) => Promise<string>, ifEmpty: string = ''): Promise<string> => {
    const elem = await get(id);
    if (!elem) {
      return ifEmpty;
    }
    return await fmt(elem);
  };

  return {
    get,
    toString,
    getStored,
  };
}
